import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ToastItem } from './toast-types';

/**
 * Callers pass caught values straight through (`toast.error(err)`), and React
 * throws on an Error or plain object rendered as a child — which unmounts the
 * whole app. Anything that is not renderable is turned into text here, so a
 * failed request shows a message instead of blanking the page.
 */
const toastText = (value: React.ReactNode): React.ReactNode => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (React.isValidElement(value) || Array.isArray(value)) return value;
  if (value instanceof Error) return value.message || String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

interface ToastCardProps {
  toast: ToastItem;
  onDismiss: (id: string | number) => void;
}

export const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    toast.duration && isFinite(toast.duration) ? toast.duration : 0,
  );
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!toast.duration || !isFinite(toast.duration) || toast.duration <= 0) {
      return;
    }

    if (isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
      if (toast.onClose) toast.onClose();
    }, remainingTime);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPaused, remainingTime, toast, onDismiss]);

  const handleMouseEnter = () => {
    if (toast.pauseOnHover !== false && toast.duration && isFinite(toast.duration)) {
      const elapsed = Date.now() - startTimeRef.current;
      setRemainingTime((prev) => Math.max(0, prev - elapsed));
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (toast.pauseOnHover !== false && toast.duration && isFinite(toast.duration)) {
      setIsPaused(false);
    }
  };

  // Status icon & accents
  const getTypeConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: (
            <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
          accentBorder: 'border-slate-700/80 hover:border-emerald-500/40',
        };
      case 'error':
        return {
          icon: (
            <div className="w-5 h-5 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
          accentBorder: 'border-slate-700/80 hover:border-rose-500/40',
        };
      case 'warning':
        return {
          icon: (
            <div className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
          accentBorder: 'border-slate-700/80 hover:border-amber-500/40',
        };
      case 'info':
        return {
          icon: (
            <div className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
          accentBorder: 'border-slate-700/80 hover:border-sky-500/40',
        };
      default:
        return {
          icon: (
            <div className="w-5 h-5 rounded-full bg-slate-700 text-slate-300 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
          accentBorder: 'border-slate-700/80',
        };
    }
  };

  const config = getTypeConfig();
  const displayIcon = toast.icon !== undefined ? toast.icon : config.icon;
  const isTop = (toast.position || 'top-right').startsWith('top');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: isTop ? -24 : 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.9,
        y: isTop ? -16 : 16,
        transition: { duration: 0.18, ease: 'easeOut' },
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
        mass: 0.8,
      }}
      drag="x"
      dragConstraints={{ left: 0, right: 300 }}
      dragElastic={{ left: 0.05, right: 0.8 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > 90 || info.velocity.x > 350) {
          onDismiss(toast.id);
          if (toast.onClose) toast.onClose();
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => {
        if (toast.onClick) toast.onClick();
        if (toast.closeOnClick) {
          onDismiss(toast.id);
          if (toast.onClose) toast.onClose();
        }
      }}
      className={`group relative flex items-center justify-between gap-3 min-w-[320px] max-w-[440px] w-full px-4 py-3.5 rounded-xl bg-[#0c121e]/95 backdrop-blur-xl border ${config.accentBorder} shadow-[0_12px_32px_rgba(0,0,0,0.45)] text-slate-100 transition-colors pointer-events-auto select-none ${
        toast.className || ''
      }`}
      style={{
        boxShadow:
          '0 10px 30px -5px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06)',
      }}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {displayIcon && <div className="mt-0.5">{displayIcon}</div>}
        <div className="flex flex-col min-w-0 flex-1">
          {toast.title ? (
            <>
              <div className="text-[13px] font-semibold text-slate-100 leading-tight truncate">
                {toast.title}
              </div>
              <div className="text-[12px] text-slate-400 mt-0.5 leading-snug break-words">
                {toastText(toast.message)}
              </div>
            </>
          ) : (
            <div className="text-[13px] font-medium text-slate-100 leading-snug break-words">
              {toastText(toast.message)}
            </div>
          )}
          {toast.description && (
            <div className="text-[11px] text-slate-400 mt-1 leading-normal">
              {toast.description}
            </div>
          )}
        </div>
      </div>

      {/* Action Button (e.g. "Undo" as in reference image) */}
      {toast.action && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toast.action?.onClick(e);
            onDismiss(toast.id);
          }}
          className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#4eed9e] text-[#081812] hover:bg-[#3be090] active:scale-95 transition-all shadow-sm ${
            toast.action.className || ''
          }`}
        >
          {toast.action.label}
        </button>
      )}

      {/* Dismiss Button */}
      {toast.dismissible && !toast.action && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
            if (toast.onClose) toast.onClose();
          }}
          className="shrink-0 p-1 text-slate-500 hover:text-slate-300 rounded-md transition-colors opacity-70 group-hover:opacity-100"
          aria-label="Close notification"
        >
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </motion.div>
  );
};
